import React, { useEffect, useMemo, useState, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  Button,
  IconButton,
  Icon,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input
} from '@shopflo/ui'
import { PlusCircle, Trash, X, Icon as PhosphorIcon, Info } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import { errorToast } from 'src/lib/utils/toasters'
import { classNames } from 'src/lib/utils/helpers'

import { UTMType, UTMOption, DEFAULT_UTM_OPTIONS } from './types'

interface UTMField {
  type: UTMType
  value: string
  error?: string
}

export interface CommonUtmSelectorDialogUiConfig {
  title: string
  description?: string
  icon?: PhosphorIcon
  utmOptions?: UTMOption[]
}

interface CommonUtmSelectorDialogProps {
  isOpen: boolean
  onClose: () => void
  initialData: Record<string, string>
  onSave: (data: Record<string, string>) => void | Promise<void>
  uiConfig: CommonUtmSelectorDialogUiConfig
}

const CommonUtmSelectorDialog: React.FC<CommonUtmSelectorDialogProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave,
  uiConfig
}) => {
  const { t } = useLocale()
  const { title, description, icon, utmOptions = DEFAULT_UTM_OPTIONS } = uiConfig

  const initialFields = useMemo<UTMField[]>(() => {
    const isDataEmpty =
      !initialData || !Object.keys(initialData).length || Object.values(initialData).every(value => !value)

    if (isDataEmpty) {
      return [{ type: utmOptions[0].value, value: '', error: '' }]
    }

    return utmOptions
      .map(option => ({
        type: option.value,
        value: initialData[option.value] ?? '',
        error: ''
      }))
      .filter(field => field.value !== '')
  }, [initialData, utmOptions])

  const [utmFields, setUtmFields] = useState<UTMField[]>(initialFields)
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (isOpen) {
      setUtmFields(initialFields)
    }
  }, [isOpen, initialFields])

  const handleAddField = () => {
    const usedTypes = new Set(utmFields.map(field => field.type))
    const nextAvailableOption = utmOptions.find(option => !usedTypes.has(option.value))

    if (nextAvailableOption) {
      setUtmFields(prev => [...prev, { type: nextAvailableOption.value, value: '', error: '' }])
    }
  }

  const handleRemoveField = (indexToRemove: number) => {
    setUtmFields(prevFields => {
      const remaining = prevFields.filter((_, index) => index !== indexToRemove)
      if (remaining.length === 0) {
        return [{ type: utmOptions[0].value, value: '', error: '' }]
      }
      return remaining
    })
  }

  const handleTypeChange = (indexToChange: number, newType: UTMType) => {
    setUtmFields(prevFields =>
      prevFields.map((field, index) => (index === indexToChange ? { ...field, type: newType, error: '' } : field))
    )
  }

  const handleValueChange = (indexToChange: number, newValue: string) => {
    setUtmFields(prevFields =>
      prevFields.map((field, index) =>
        index === indexToChange ? { ...field, value: newValue, error: newValue.trim() ? '' : field.error } : field
      )
    )
  }

  const getAvailableOptions = (currentType: UTMType) => {
    const usedTypes = new Set(utmFields.map(field => field.type))
    return utmOptions.filter(option => !usedTypes.has(option.value) || option.value === currentType)
  }

  const handleSave = async () => {
    const nonEmptyFields = utmFields.filter(field => field.value.trim())
    const emptyWithContent = utmFields.filter(field => !field.value.trim())

    if (nonEmptyFields.length === 0 && utmFields.length === 1) {
      try {
        await onSave({})
        onClose()
      } catch (error: any) {
        errorToast(error?.message ?? 'Something went wrong while saving.')
      }
      return
    }

    let hasError = false
    const validatedFields = utmFields.map(field => {
      if (!field.value.trim()) {
        hasError = true
        return { ...field, error: 'This field cannot be empty' }
      }
      return { ...field, error: '' }
    })

    setUtmFields(validatedFields)

    if (hasError) {
      errorToast('Please fill all UTM field values before saving.')
      return
    }

    const payload: Record<string, string> = {}

    validatedFields.forEach(field => {
      payload[field.type] = field.value
    })

    try {
      await onSave(payload)
      onClose()
    } catch (error: any) {
      errorToast(error?.message ?? 'Something went wrong while saving.')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className='max-w-[620px] bg-white !rounded-2xl px-5 py-4'>
        <DialogHeader>
          <div className='flex justify-between items-center w-full'>
            <div className='flex items-center gap-2'>
              {icon && (
                <div className='bg-zinc-500/10 p-1.5 rounded-xl'>
                  <Icon IconComponent={icon} size='lg' iconProps={{ weight: 'duotone' }} className='text-zinc-500' />
                </div>
              )}
              <span className='text-text-lg-semibold text-zinc-800'>{title}</span>
            </div>
            <IconButton IconComponent={X} size='sm' variant='ghost' onClick={onClose} />
          </div>
        </DialogHeader>

        {description && (
          <div className='flex items-center gap-2 bg-sky-600/10 px-2 py-1.5 rounded-lg mt-1'>
            <Icon IconComponent={Info} size='sm' iconProps={{ weight: 'duotone' }} className='text-sky-600' />
            <div className='text-text-xs-regular text-zinc-600'>{description}</div>
          </div>
        )}

        <div className='flex flex-col gap-4'>
          {utmFields.map((field, index) => (
            <div key={index} className='flex flex-col gap-1'>
              <div className='flex gap-3 items-start'>
                <Select value={field.type} onValueChange={val => handleTypeChange(index, val as UTMType)}>
                  <SelectTrigger className='h-9 w-1/3 rounded-[8px]'>
                    <SelectValue placeholder='Select UTM' />
                  </SelectTrigger>
                  <SelectContent className='rounded-[8px]'>
                    {getAvailableOptions(field.type).map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.labelKey ? t(option.labelKey) : option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className='flex-1'>
                  <div className='flex flex-col'>
                    <Input
                      dimension='md'
                      className={classNames('h-9', field.error ? 'border-red-500' : '')}
                      value={field.value}
                      error={!!field.error}
                      onChange={e => handleValueChange(index, e.target.value)}
                      onFocus={() => setFocusedIndex(index)}
                      onBlur={() => setFocusedIndex(prev => (prev === index ? null : prev))}
                      placeholder='Enter values (e.g. google, facebook)'
                      ref={el => {
                        inputRefs.current[index] = el
                      }}
                    />
                    <div
                      className={classNames(
                        'text-text-xs-regular text-zinc-500 transition-all duration-300 overflow-hidden',
                        focusedIndex === index ? 'opacity-100 max-h-6 mt-1' : 'opacity-0 max-h-0 mt-0'
                      )}
                    >
                      {t('shopass_analytics.use_comma')}
                    </div>
                  </div>
                </div>

                <div className='flex min-h-[36px] items-center'>
                  <IconButton
                    IconComponent={Trash}
                    size='sm'
                    variant='ghost'
                    onClick={() => handleRemoveField(index)}
                    className='h-fit'
                    disabled={utmFields.length === 1 && !field.value.trim()}
                  />
                </div>
              </div>
            </div>
          ))}

          {utmFields.length < utmOptions.length && (
            <Button variant='ghost' onClick={handleAddField} leftIcon={PlusCircle} className='w-fit'>
              {t('shopass_analytics.add_another')}
            </Button>
          )}
        </div>

        <DialogFooter className='mt-1'>
          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={onClose} size='md'>
              {t('cancel')}
            </Button>
            <Button variant='primary' onClick={handleSave} size='md'>
              {t('save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CommonUtmSelectorDialog
